import { useEffect, useState } from 'react'
import { Upload } from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface ImportCsvModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  templateUrl: string
  importUrl: string
  hint?: string
  onSuccess: () => void
}

export function ImportCsvModal({
  isOpen,
  onClose,
  title,
  templateUrl,
  importUrl,
  hint,
  onSuccess,
}: ImportCsvModalProps) {
  const [csv, setCsv] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ created: number; failed: number; errors: any[]; message?: string; models?: number } | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setCsv('')
    setResult(null)
  }, [isOpen])

  const downloadTemplate = async () => {
    const response = await apiClient.get(templateUrl, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'import-template.csv'
    link.click()
    window.URL.revokeObjectURL(url)
  }

  const handleFile = async (file?: File | null) => {
    if (!file) return
    setCsv(await file.text())
  }

  const handleImport = async () => {
    if (!csv.trim()) {
      toast.error('Paste CSV content or choose a file first')
      return
    }
    setLoading(true)
    try {
      const response = await apiClient.post(importUrl, { csv })
      const data = response.data
      setResult(data)
      if (data.failed > 0) {
        toast.error(data.message || `Import failed for ${data.failed} row(s)`)
        if (data.created > 0) onSuccess()
        return
      }
      toast.success(data.message || `Imported ${data.created} row(s)`)
      onSuccess()
    } catch (error: any) {
      const data = error.response?.data
      if (data?.errors?.length) {
        setResult({
          created: data.created || 0,
          models: data.models || 0,
          failed: data.failed || data.errors.length,
          errors: data.errors,
          message: data.message,
        })
      }
      toast.error(data?.message || 'Import failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {hint || 'Download the template, fill it in, then upload or paste the CSV below.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={downloadTemplate}>
            Download template
          </Button>
          <label className="inline-flex cursor-pointer items-center rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent">
            <Upload className="mr-2 h-4 w-4" />
            Choose CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => handleFile(event.target.files?.[0])}
            />
          </label>
        </div>
        <textarea
          value={csv}
          onChange={(event) => setCsv(event.target.value)}
          rows={10}
          className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
          placeholder="Paste CSV here..."
        />
        {result && (
          <div className="rounded-xl border border-border/80 bg-muted/40 p-3 text-sm">
            {result.message && <p className="mb-1">{result.message}</p>}
            <p>Created: {result.created}</p>
            {typeof result.models === 'number' && <p>New models: {result.models}</p>}
            <p>Failed: {result.failed}</p>
            {result.errors?.length > 0 && (
              <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto text-destructive">
                {result.errors.map((error) => (
                  <li key={`${error.row}-${error.message}`}>
                    Row {error.row}: {error.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="button" onClick={handleImport} disabled={loading}>
            {loading ? 'Importing...' : 'Import CSV'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
